import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { BrowserSettings, BrowserTab, ConsoleEntry, ErrorPageKind, InstalledExtension, OfflinePageRecord, RenderMode } from '../types';
import { findOfflinePageByUrl, getOfflinePage } from '../lib/db';
import { dictionaryFor } from '../lib/translate';
import { scriptsForUrl } from '../lib/extensions';
import { effectiveRenderMode, gameResolveUrl, isInternalUrl, proxiedUrl, shouldUseCrazyGameCompanion, shouldUseGameCompatibility, shouldUseNativeGameLaunch, shouldUseYouTubeCompanion, toDisplayUrl } from '../lib/urls';
import { ErrorDocumentFrame } from './ErrorDocumentFrame';
import { ReadingView } from './ReadingView';
import { DevTools } from './DevTools';
import { HtmlSandbox } from './HtmlSandbox';
import { YouTubeCompanion } from './YouTubeCompanion';
import { CrazyGameCompanion } from './CrazyGameCompanion';

interface Props {
  tab: BrowserTab;
  settings: BrowserSettings;
  extensions: InstalledExtension[];
  consoleEntries: ConsoleEntry[];
  translateTarget: string;
  renderInternal: () => ReactNode;
  onLoaded: (tabId: string, payload: { title?: string; url?: string; favicon?: string }) => void;
  onNavigate: (tabId: string, url: string) => void;
  onNewTab: (url: string) => void;
  onError: (tabId: string, kind: ErrorPageKind) => void;
  onConsole: (entry: Omit<ConsoleEntry, 'id' | 'createdAt'>) => void;
  onUseOfflineSnapshot: (tabId: string, snapshotId: string) => void;
  onRetry: (tabId: string) => void;
  onSetRenderMode: (tabId: string, mode: RenderMode) => void;
}

interface GameResolveCandidate {
  url: string;
  label: string;
  confidence: number;
}

interface GameResolveResponse {
  sourceUrl: string;
  candidates: GameResolveCandidate[];
  warning?: string;
}

function GameNativePane({ tab, onNavigate, onSetRenderMode }: { tab: BrowserTab; onNavigate: (tabId: string, url: string) => void; onSetRenderMode: (tabId: string, mode: RenderMode) => void }) {
  const [resolving, setResolving] = useState(false);
  const [message, setMessage] = useState('');
  const [candidates, setCandidates] = useState<GameResolveCandidate[]>([]);

  function openNative(kind: 'tab' | 'current') {
    if (kind === 'current') {
      window.location.href = tab.url;
      return;
    }
    const opened = window.open(tab.url, '_blank', 'noopener,noreferrer');
    if (!opened) setMessage('Popup blocked. Use the “Open in this Chrome tab” button instead.');
  }

  async function resolveGameUrl() {
    setResolving(true);
    setMessage('Resolving the direct game file…');
    setCandidates([]);
    try {
      const response = await fetch(gameResolveUrl(tab.url));
      const data = await response.json() as GameResolveResponse;
      if (!response.ok) throw new Error(data.warning || 'Game resolver failed.');
      if (!data.candidates.length) {
        setMessage('No direct game frame was found. Use Native Launch for this game.');
        return;
      }
      setCandidates(data.candidates);
      setMessage('Direct game candidates found. Try the top result first.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Game resolver failed.');
    } finally {
      setResolving(false);
    }
  }

  const gameLabel = toDisplayUrl(tab.url);

  return (
    <div className="browser-pane native-game-pane">
      <section className="native-game-card">
        <div className="native-game-mark">▶</div>
        <p className="eyebrow">Native Game Launch</p>
        <h1>{gameLabel}</h1>
        <p>
          This site is a game portal. Some game pages keep showing “Preparing your game…” when they are embedded in a proxy or iframe.
          Native Launch opens the page as a real top-level browser page, which is the most reliable way to play WebGL, Unity, WASM, multiplayer, and login-based games.
        </p>
        <div className="native-game-actions">
          <button className="primary" onClick={() => openNative('tab')}>Open playable game tab</button>
          <button onClick={() => openNative('current')}>Open in this Chrome tab</button>
          <button onClick={() => { onSetRenderMode(tab.id, 'proxy'); }}>Try WebSurf proxy anyway</button>
          <button onClick={() => { onSetRenderMode(tab.id, 'direct'); }}>Try direct iframe anyway</button>
        </div>
        <div className="game-resolver-panel">
          <div>
            <strong>Experimental direct game-file resolver</strong>
            <span>Attempts to extract a direct game iframe/file URL and open that in WebSurf.</span>
          </div>
          <button onClick={resolveGameUrl} disabled={resolving}>{resolving ? 'Resolving…' : 'Resolve game URL'}</button>
        </div>
        {message && <p className="resolver-message">{message}</p>}
        {candidates.length > 0 && (
          <div className="candidate-list">
            {candidates.map((candidate) => (
              <button key={candidate.url} onClick={() => { onSetRenderMode(tab.id, 'direct'); onNavigate(tab.id, candidate.url); }}>
                <span>{candidate.label}</span>
                <small>{toDisplayUrl(candidate.url)}</small>
              </button>
            ))}
          </div>
        )}
        <p className="native-game-note">
          Tip: use this mode for CrazyGames, Poki, Hypackel, and other portals when the embedded game loader does not finish.
        </p>
      </section>
    </div>
  );
}

export function BrowserPane({ tab, settings, extensions, consoleEntries, translateTarget, renderInternal, onLoaded, onNavigate, onError, onUseOfflineSnapshot, onRetry, onSetRenderMode }: Props) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [snapshot, setSnapshot] = useState<OfflinePageRecord | undefined>();
  const [showBlankHint, setShowBlankHint] = useState(false);
  const isInternal = isInternalUrl(tab.url);
  const extensionScripts = useMemo(() => scriptsForUrl(extensions, tab.url), [extensions, tab.url]);
  const renderMode = isInternal ? 'proxy' : effectiveRenderMode(tab.url, tab.renderMode, settings.defaultRenderMode || 'smart');
  const gameCompatibility = !isInternal && shouldUseGameCompatibility(tab.url);
  const nativeGameLaunch = !isInternal && shouldUseNativeGameLaunch(tab.url) && renderMode === 'native';
  const useYouTubeCompanion = !isInternal && shouldUseYouTubeCompanion(tab.url, tab.renderMode) && tab.viewMode === 'browser' && !tab.error && !tab.offlineSnapshotId;
  const useCrazyGameCompanion = !isInternal && shouldUseCrazyGameCompanion(tab.url, tab.renderMode) && tab.viewMode === 'browser' && !tab.error && !tab.offlineSnapshotId;

  useEffect(() => {
    let cancelled = false;
    setSnapshot(undefined);
    if (tab.offlineSnapshotId) {
      getOfflinePage(tab.offlineSnapshotId).then((record) => { if (!cancelled) setSnapshot(record); });
      return () => { cancelled = true; };
    }
    if (!navigator.onLine && !isInternal && tab.viewMode === 'browser') {
      findOfflinePageByUrl(tab.url).then((record) => {
        if (cancelled) return;
        if (record) onUseOfflineSnapshot(tab.id, record.id);
        else onError(tab.id, 'offline');
      });
    }
    return () => { cancelled = true; };
  }, [tab.id, tab.url, tab.offlineSnapshotId, tab.viewMode, isInternal, onError, onUseOfflineSnapshot]);

  useEffect(() => {
    if (isInternal || nativeGameLaunch || useYouTubeCompanion || useCrazyGameCompanion || tab.error || tab.offlineSnapshotId || tab.viewMode !== 'browser') return;
    const timeout = gameCompatibility ? Math.max(settings.proxyTimeoutMs, 120000) : renderMode === 'direct' ? Math.max(settings.proxyTimeoutMs, 60000) : settings.proxyTimeoutMs;
    const timer = window.setTimeout(() => {
      if (tab.status === 'loading') onError(tab.id, 'timeout');
    }, timeout);
    return () => window.clearTimeout(timer);
  }, [isInternal, nativeGameLaunch, useYouTubeCompanion, useCrazyGameCompanion, settings.proxyTimeoutMs, tab.error, tab.id, tab.offlineSnapshotId, tab.status, tab.url, tab.viewMode, renderMode, gameCompatibility, onError]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame?.contentWindow || renderMode === 'direct' || renderMode === 'native') return;
    frame.contentWindow.postMessage({ type: 'websurf:extensions', scripts: extensionScripts }, '*');
    if (translateTarget !== 'en') frame.contentWindow.postMessage({ type: 'websurf:translate', dictionary: dictionaryFor(translateTarget) }, '*');
  }, [extensionScripts, translateTarget, tab.url, tab.status, renderMode]);

  useEffect(() => {
    setShowBlankHint(false);
    if (isInternal || nativeGameLaunch || useYouTubeCompanion || useCrazyGameCompanion || tab.error || tab.offlineSnapshotId || tab.viewMode !== 'browser') return;
    const timer = window.setTimeout(() => {
      if (tab.status === 'loading' || (renderMode === 'direct')) setShowBlankHint(true);
    }, renderMode === 'direct' ? 2000 : 6000);
    return () => window.clearTimeout(timer);
  }, [isInternal, nativeGameLaunch, useYouTubeCompanion, useCrazyGameCompanion, tab.error, tab.offlineSnapshotId, tab.viewMode, tab.status, tab.url, tab.reloadKey, renderMode]);

  if (isInternal) return <div className="browser-pane internal-pane">{renderInternal()}</div>;
  if (tab.viewMode === 'reading') return <ReadingView url={tab.url} onBack={() => onNavigate(tab.id, tab.url)} />;
  if (tab.viewMode === 'devtools') return <DevTools tab={tab} consoleEntries={consoleEntries.filter((entry) => entry.tabId === tab.id)} />;
  if (tab.viewMode === 'sandbox') return <HtmlSandbox />;
  if (tab.error) return <ErrorDocumentFrame kind={tab.error} onRetry={() => onRetry(tab.id)} />;
  if (snapshot) return <iframe title={`${snapshot.title} offline`} className="browser-frame offline-frame" srcDoc={snapshot.html} sandbox="allow-scripts allow-forms allow-popups allow-downloads" />;
  if (useYouTubeCompanion) return <YouTubeCompanion tab={tab} onLoaded={onLoaded} onNavigate={onNavigate} />;
  if (useCrazyGameCompanion) return <CrazyGameCompanion tab={tab} onLoaded={onLoaded} onNavigate={onNavigate} onSetRenderMode={onSetRenderMode} />;
  if (nativeGameLaunch) return <GameNativePane tab={tab} onNavigate={onNavigate} onSetRenderMode={onSetRenderMode} />;

  const src = renderMode === 'direct' ? tab.url : `${proxiedUrl(tab.url)}#websurf-tab=${encodeURIComponent(tab.id)}`;
  const sandboxPolicy = [
    'allow-scripts',
    'allow-forms',
    'allow-popups',
    'allow-modals',
    'allow-downloads',
    'allow-popups-to-escape-sandbox',
    'allow-top-navigation-by-user-activation',
    'allow-pointer-lock',
    'allow-presentation',
    'allow-same-origin'
  ].join(' ');

  const framePermissions = [
    'accelerometer *',
    'autoplay *',
    'camera *',
    'clipboard-read *',
    'clipboard-write *',
    'encrypted-media *',
    'fullscreen *',
    'gamepad *',
    'geolocation *',
    'gyroscope *',
    'microphone *',
    'payment *',
    'picture-in-picture *',
    'screen-wake-lock *',
    'storage-access *',
    'web-share *',
    'xr-spatial-tracking *'
  ].join('; ');

  const iframeSandboxProps = renderMode === 'direct' && gameCompatibility ? {} : { sandbox: sandboxPolicy };

  return (
    <div className={`browser-pane ${renderMode === 'direct' ? 'direct-render-pane' : 'proxy-render-pane'} ${gameCompatibility ? 'game-compat-pane' : ''}`}>
      {tab.status === 'loading' && <div className="progress-bar" />}
      {renderMode === 'direct' && gameCompatibility && <div className="render-mode-badge game-badge">Game Direct · sandbox removed for WebGL, controls, audio, fullscreen, and login popups</div>}
      {renderMode === 'direct' && !gameCompatibility && <div className="render-mode-badge">Direct Mode · best for sites that already allow iframes</div>}
      {renderMode === 'proxy' && gameCompatibility && <div className="render-mode-badge game-badge">Game Boost Proxy · strips frame blockers and rewrites game assets</div>}
      <iframe
        key={`${tab.id}:${tab.url}:${tab.zoom}:${tab.reloadKey || 0}:${renderMode}`}
        ref={frameRef}
        title={tab.title || tab.url}
        className={`${settings.compatibilityMode ? 'browser-frame compatibility-frame' : 'browser-frame'} ${renderMode === 'direct' ? 'direct-frame' : 'proxy-frame'}`}
        src={src}
        style={{ transform: `scale(${tab.zoom})`, width: `${100 / tab.zoom}%`, height: `${100 / tab.zoom}%`, transformOrigin: '0 0' }}
        onLoad={() => {
          onLoaded(tab.id, {});
          if (renderMode === 'proxy') {
            const frame = frameRef.current;
            frame?.contentWindow?.postMessage({ type: 'websurf:extensions', scripts: extensionScripts }, '*');
            if (translateTarget !== 'en') frame?.contentWindow?.postMessage({ type: 'websurf:translate', dictionary: dictionaryFor(translateTarget) }, '*');
          }
        }}
        {...iframeSandboxProps}
        allow={framePermissions}
        referrerPolicy={renderMode === 'direct' ? 'strict-origin-when-cross-origin' : 'no-referrer'}
        allowFullScreen
        loading="eager"
      />
      {showBlankHint && (
        <div className="blank-frame-hint" role="status">
          <span>
            {renderMode === 'direct'
              ? 'This site may block iframe embedding. Try '
              : 'Page is taking a while. Try '}
          </span>
          <button onClick={() => { setShowBlankHint(false); onSetRenderMode(tab.id, renderMode === 'direct' ? 'proxy' : 'direct'); }}>
            switch to {renderMode === 'direct' ? 'Proxy' : 'Direct'} mode
          </button>
          <button onClick={() => setShowBlankHint(false)} aria-label="Dismiss" className="blank-frame-dismiss">✕</button>
        </div>
      )}
    </div>
  );
}
